"use client";

// Web variant of <Explainable>. See Explainable.tsx for the native version
// and the architecture note. This file uses Modal (desktop + mobile-web
// friendly) and pure DOM/CSS so it never imports Reanimated / Gesture
// Handler / Expo Haptics — the per-platform export contract in
// packages/ui/package.json keeps webpack out of the RN tree.

import { useCallback, useState, type CSSProperties, type ReactNode } from "react";
import Modal from "../Modal";
import type {
  ExplainerCTA,
  ExplainerIntensity,
  ExplainerIntensityKey,
  ExplainerResponse,
  ExplainerType,
} from "@ishvaram/api-client";

export type ExplainableTrackEvent =
  | "explainable.opened"
  | "explainable.cta_clicked"
  | "explainable.dismissed";

export interface ExplainableProps {
  type: ExplainerType;
  id: string;
  intensity?: ExplainerIntensityKey;
  triggerLabel?: string;
  children?: ReactNode;
  data?: ExplainerResponse;
  loading?: boolean;
  error?: Error | null;
  onRequestData?: () => void;
  onTrack?: (event: ExplainableTrackEvent, props: Record<string, unknown>) => void;
  onCtaPress?: (cta: ExplainerCTA) => void;
  /** Called with true/false when the user taps the thumbs row inside the sheet. */
  onFeedback?: (helpful: boolean) => void;
  surface?: string;
  /** Web-only: extra className on trigger element. */
  className?: string;
}

const triggerStyle: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "var(--ish-brand, #b9644a)",
  textDecoration: "underline dotted",
  cursor: "pointer",
  font: "inherit",
  display: "inline",
};

const sectionLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--ish-text-muted, #8a7968)",
  marginTop: 16,
  marginBottom: 6,
};

const bodyStyle: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.55,
  color: "var(--ish-dark, #232323)",
  margin: 0,
  marginBottom: 8,
  whiteSpace: "pre-wrap",
};

const upayBlockStyle: CSSProperties = {
  paddingTop: 12,
  paddingBottom: 12,
  borderBottom: "1px solid var(--ish-border, #ebe1d6)",
};

const ctaStyle: CSSProperties = {
  marginTop: 24,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderRadius: 12,
  background: "var(--ish-brand, #b9644a)",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 600,
};

export default function Explainable({
  type,
  id,
  intensity,
  triggerLabel,
  children,
  data,
  loading = false,
  error = null,
  onRequestData,
  onTrack,
  onCtaPress,
  onFeedback,
  surface,
  className,
}: ExplainableProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (!data) onRequestData?.();
    setOpen(true);
    onTrack?.("explainable.opened", { type, id, intensity, surface });
  }, [data, intensity, id, onRequestData, onTrack, surface, type]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onTrack?.("explainable.dismissed", { type, id, surface });
  }, [id, onTrack, surface, type]);

  const handleCtaPress = useCallback(() => {
    if (!data) return;
    onTrack?.("explainable.cta_clicked", {
      type,
      id,
      surface,
      product: data.cta.product,
    });
    onCtaPress?.(data.cta);
  }, [data, id, onCtaPress, onTrack, surface, type]);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={className}
        style={triggerStyle}
        aria-haspopup="dialog"
        aria-label={triggerLabel ?? id}
      >
        {children ?? triggerLabel ?? id}
      </button>
      <Modal
        visible={open}
        onClose={handleClose}
        title={data?.display_name ?? "Loading…"}
        size="md"
      >
        <SheetBody
          data={data}
          loading={loading}
          error={error}
          intensity={intensity}
          onCtaPress={handleCtaPress}
          onFeedback={onFeedback}
        />
      </Modal>
    </>
  );
}

interface SheetBodyProps {
  data?: ExplainerResponse;
  loading: boolean;
  error: Error | null;
  intensity?: ExplainerIntensityKey;
  onCtaPress: () => void;
  onFeedback?: (helpful: boolean) => void;
}

function SheetBody({ data, loading, error, intensity, onCtaPress, onFeedback }: SheetBodyProps) {
  const [thumbState, setThumbState] = useState<"idle" | "up" | "down">("idle");

  const handleThumb = useCallback(
    (helpful: boolean) => {
      if (thumbState !== "idle") return;
      setThumbState(helpful ? "up" : "down");
      onFeedback?.(helpful);
    },
    [onFeedback, thumbState]
  );

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <p style={{ ...bodyStyle, fontWeight: 600 }}>Could not load explanation</p>
        <p style={bodyStyle}>Please close and try again.</p>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <p style={bodyStyle}>Loading meaning&hellip;</p>
      </div>
    );
  }

  const intensityBucket: ExplainerIntensity | null =
    data.intensity ??
    (intensity && data.intensity_buckets ? data.intensity_buckets[intensity] : null);

  return (
    <div style={{ overflowY: "auto" }}>
      {data.display_name_hi ? (
        <p style={{ ...bodyStyle, color: "var(--ish-text-muted, #8a7968)" }}>
          {data.display_name_hi}
        </p>
      ) : null}

      <p style={sectionLabelStyle}>What it means</p>
      <p style={bodyStyle}>{data.what_it_means}</p>

      {intensityBucket ? (
        <>
          <p style={sectionLabelStyle}>For your chart &mdash; {intensityBucket.label}</p>
          <p style={bodyStyle}>{intensityBucket.prose}</p>
        </>
      ) : null}

      <p style={sectionLabelStyle}>Recommended upays</p>
      {data.upays.map((upay, idx) => (
        <div key={idx} style={upayBlockStyle}>
          <p style={{ ...bodyStyle, fontWeight: 600 }}>{upay.title}</p>
          <p style={{ ...bodyStyle, fontSize: 14, color: "var(--ish-text-muted, #6a5d4f)" }}>
            {upay.why}
          </p>
          <p style={{ ...bodyStyle, fontSize: 14, color: "var(--ish-text-muted, #6a5d4f)" }}>
            {upay.how}
          </p>
        </div>
      ))}

      {onFeedback != null && (
        <div
          style={{
            marginTop: 20,
            display: "flex",
            alignItems: "center",
            gap: 10,
            justifyContent: "center",
          }}
        >
          {thumbState === "idle" ? (
            <>
              <span style={{ fontSize: 13, color: "var(--ish-text-muted, #8a7968)" }}>
                Was this helpful?
              </span>
              <button
                type="button"
                aria-label="Yes, helpful"
                onClick={() => handleThumb(true)}
                style={{
                  background: "none",
                  border: "1px solid var(--ish-border, #ebe1d6)",
                  borderRadius: 8,
                  padding: "6px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* Phosphor thumbs-up */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 256 256"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ color: "var(--ish-brand, #b9644a)" }}
                >
                  <path d="M234,80.12A24,24,0,0,0,216,72H160V56a40,40,0,0,0-40-40,8,8,0,0,0-7.16,4.42L75.06,96H32a16,16,0,0,0-16,16v88a16,16,0,0,0,16,16H204a24,24,0,0,0,23.82-21l12-96A24,24,0,0,0,234,80.12ZM32,112H72v88H32Zm191.94,1.42-12,96A8,8,0,0,1,204,216H88V105.89l36.71-73.43A24,24,0,0,1,144,56V80a8,8,0,0,0,8,8h64a8,8,0,0,1,7.94,9.42Z" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="No, not helpful"
                onClick={() => handleThumb(false)}
                style={{
                  background: "none",
                  border: "1px solid var(--ish-border, #ebe1d6)",
                  borderRadius: 8,
                  padding: "6px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* Phosphor thumbs-down */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 256 256"
                  fill="currentColor"
                  aria-hidden="true"
                  style={{ color: "var(--ish-text-muted, #8a7968)" }}
                >
                  <path d="M239.82,157l-12-96A24,24,0,0,0,204,40H32A16,16,0,0,0,16,56v88a16,16,0,0,0,16,16H75.06l37.78,75.58A8,8,0,0,0,120,240a40,40,0,0,0,40-40V184h56a24,24,0,0,0,23.82-27ZM72,144H32V56H72Zm150,21.29A7.93,7.93,0,0,1,216,168H152a8,8,0,0,0-8,8v24a24,24,0,0,1-19.29,23.54L88,150.11V56H204a8,8,0,0,1,7.94,7l12,96A7.94,7.94,0,0,1,222,165.29Z" />
                </svg>
              </button>
            </>
          ) : (
            <span style={{ fontSize: 13, color: "var(--ish-text-muted, #8a7968)" }}>Thanks!</span>
          )}
        </div>
      )}

      <button type="button" onClick={onCtaPress} style={ctaStyle} aria-label={data.cta.label}>
        <span>
          {data.cta.label} {"→"}
        </span>
        <span>
          {"₹"}
          {data.cta.price_inr}
        </span>
      </button>
    </div>
  );
}
